import {FC, useCallback, useEffect, useState} from 'react'

import {yupResolver} from '@hookform/resolvers/yup'
import cn from 'classnames/bind'
import {FormProvider, useForm} from 'react-hook-form'
import * as yup from 'yup'

import FormHeader from 'components/blocks/FormHeader'
import Button from 'components/elements/Button'
import FieldInput from 'components/elements/fields/FieldInput'
import FieldTextarea from 'components/elements/fields/FieldTextarea'
import MessageBlock from 'components/elements/MessageBlock'
import apiService from 'services/api.service'
import {posthogTrack} from 'src/utils'
import {useAuthStore} from 'src/utils/contexts/RootStore.context'
import useAsyncFlow from 'src/utils/hooks/useAsyncFlow'
import {messages} from 'src/utils/validations'

import s from './ContactForm.module.scss'

const cx = cn.bind(s)

const schema = yup
	.object({
		name: yup.string().trim().required(messages.required),
		message: yup
			.string()
			.trim()
			.required(messages.required)
			.min(21, 'min length must be greater than 20'),
		email: yup.string().email('please enter a valid email').required(messages.required),
	})
	.required()

interface IContactFormProps {
	className?: string
}

const ContactForm: FC<IContactFormProps> = ({className}) => {
	const [successMessage, setSuccessMessage] = useState('')
	const authStore = useAuthStore()
	const [contactFlow, {isLoading, errorMessage}] = useAsyncFlow<typeof apiService.contact>(
		apiService.contact,
	)

	const methods = useForm<yup.TypeOf<typeof schema>>({
		resolver: yupResolver(schema),
		defaultValues: {
			email: '',
			name: '',
			message: '',
		},
	})

	useEffect(() => {
		methods.reset({
			email: authStore.user?.email || '',
			name: authStore.user?.firstName || '',
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const onSubmit = useCallback(
		async (data: yup.TypeOf<typeof schema>) => {
			const {email, name, message} = data
			posthogTrack('[Contact page] Send button was clicked')
			await contactFlow({
				name: name || '',
				email: email || '',
				message: message || '',
			})({
				onSuccess: () => {
					posthogTrack('[Contact page] Contact request was successfully sent')
					setSuccessMessage('We received your message and will get back to you soon.')
				},
			})
		},
		[contactFlow],
	)

	return (
		<div className={cx('wrapper', className)}>
			<FormProvider {...methods}>
				<form className={s.form}>
					<FormHeader
						className={cx('header')}
						title="Get in touch"
						subTitle="Feel free to contact us and we will get back to you as soon as possible"
						message={errorMessage || methods.formState.errors}
					/>
					{successMessage && (
						<MessageBlock type="success" className={cx('message')} message={successMessage} />
					)}
					<FieldInput
						wrapperClassName={cx('input-spaces')}
						name="email"
						type="email"
						label="Email Address"
						placeholder="Enter your email"
						required
					/>
					<FieldInput
						wrapperClassName={cx('input-spaces')}
						name="name"
						type="text"
						label="Name"
						placeholder="Enter your name"
						required
					/>
					<FieldTextarea
						name="message"
						label="Message"
						placeholder="Write your message here"
						rows={4}
						required
						wrapperClassName={cx('input-spaces')}
					/>
					<Button
						className={cx('btn')}
						size="big"
						onClick={methods.handleSubmit(onSubmit)}
						isLoading={isLoading}
						disabled={methods.formState.isSubmitted && !methods.formState.isValid}>
						Send
					</Button>
				</form>
			</FormProvider>
		</div>
	)
}

ContactForm.displayName = 'ContactForm'

export default ContactForm
